// Automations: one list. The starters (booking and visit messages, on from day one) and anything HQ builds are the
// same kind of row; a starter can be paused or reset, never deleted. Edit opens the builder. Sent and waiting, people
// in a flow, and Safety (the organization's sending rules) are the other tabs. Branch users can look; only HQ
// changes anything.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useSearchParams } from 'react-router-dom';
import api, { formatDateTime, formatPhone } from '../services/api';
import { useEngagementScope } from '../context/EngagementScope';
import AutomationEditor from '../components/AutomationEditor';
import BranchSelect from '../components/BranchSelect';
import { engagementPath } from '../engagementPaths';
import ReminderMessages from './Automation';
import { ChipRow, Confirm, EmptyState, Loader, Modal, PageHead, SearchBox, Spinner } from '../components/ui';

const TABS = [
  { id: 'automations', label: 'Automations', icon: '⚡' },
  { id: 'messages', label: 'Sent and waiting', icon: '📋' },
  { id: 'runs', label: 'People in a flow', icon: '🏃' },
  { id: 'safety', label: 'Safety', icon: '⚙️' },
];
// Older links (?tab=reminders, ?tab=builtin, ?tab=custom) still land on the list.
const TAB_ALIASES = { reminders: 'automations', builtin: 'automations', custom: 'automations', automations: 'automations', history: 'messages', settings: 'safety' };
const STATUS_TONE = { active: 'ok', paused: 'warn', draft: 'info', archived: 'muted', on: 'ok' };
const RUNS_PAGE = 50;

function statusLabel(row) {
  return row.status === 'active' ? 'on' : row.status;
}
const RUN_TONE = { active: 'info', waiting: 'info', completed: 'ok', stopped: 'muted', failed: 'danger', paused: 'warn' };

function Pill({ tone, children }) {
  return <span className={`de-pill de-pill--sm de-pill--${tone || 'muted'}`}>{children}</span>;
}

function Switch({ on, onChange, label, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      title={on ? `${label}: on. Click to turn off` : `${label}: off. Click to turn on`}
      disabled={disabled}
      className={`de-switch${on ? ' on' : ''}`}
      onClick={() => onChange(!on)}
    >
      <span className="de-switch-knob" />
    </button>
  );
}

/** "Only Velachery, Anna Nagar" or "All except Velachery"; "" when it runs for every branch. */
function branchLimit(branches, branchName) {
  const name = (id) => (branchName(id) || `Branch ${id}`).split(' (')[0];
  if (branches?.only?.length) return `Only ${branches.only.map(name).join(', ')}`;
  if (branches?.except?.length) return `All except ${branches.except.map(name).join(', ')}`;
  return '';
}

/** Whether customers of this branch can enter the automation. */
function runsFor(branches, franchiseId) {
  const id = Number(franchiseId);
  if (branches?.only?.length) return branches.only.includes(id);
  return !(branches?.except || []).includes(id);
}

// ── automations ──────────────────────────────────────────────────────────────

const START_FROM = [
  { id: 'scratch', label: 'From scratch', hint: 'Pick what starts it, then add the steps.' },
  { id: 'recipe', label: 'From a recipe', hint: 'A ready-made flow (win-back, upsell, lead follow-up …) as a draft.' },
  { id: 'copy', label: 'Copy one', hint: 'A draft copy of an automation you already have.' },
];

function NewAutomationModal({ catalog, rows, onClose, onCreated }) {
  const [from, setFrom] = useState('scratch');
  const [form, setForm] = useState({ name: '', folder: 'Custom', trigger: catalog.triggers[0]?.type || 'manual' });
  const [recipes, setRecipes] = useState(null);
  const [recipe, setRecipe] = useState('');
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (from === 'recipe' && !recipes) {
      api
        .listRecipes()
        .then((list) => {
          setRecipes(list);
          setRecipe((current) => current || list[0]?.key || '');
        })
        .catch((e) => toast.error(e.message));
    }
  }, [from, recipes]);

  const create = async () => {
    setBusy(true);
    try {
      let row;
      if (from === 'recipe') row = await api.createFromRecipe(recipe);
      else if (from === 'copy') row = await api.duplicateAutomation(source);
      else {
        row = await api.createAutomation({
          name: form.name,
          folder: form.folder,
          definition: {
            trigger: { type: form.trigger, also: [], filters: {} },
            conditions: { all: [] },
            steps: [],
            stop_on: ['opted_out'],
            settings: { re_entry: 'once_per_booking', re_entry_days: 0, branches: [], exclude_branches: [] },
          },
        });
      }
      onCreated(row);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const ready = from === 'scratch' ? Boolean(form.name.trim()) : from === 'recipe' ? Boolean(recipe) : Boolean(source);
  const triggerLabel = (type) => catalog.triggers.find((t) => t.type === type)?.label || type;
  return (
    <Modal title="New automation" onClose={onClose} size="wide">
      <div className="de-row" style={{ gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        {START_FROM.map((option) => (
          <button
            key={option.id}
            type="button"
            className={`de-card de-choice${from === option.id ? ' active' : ''}`}
            style={{ flex: '1 1 180px', textAlign: 'left', cursor: 'pointer', borderColor: from === option.id ? 'var(--de-primary, #dc2626)' : undefined }}
            aria-pressed={from === option.id}
            onClick={() => setFrom(option.id)}
          >
            <div className="de-td-strong">{option.label}</div>
            <div className="de-small de-muted">{option.hint}</div>
          </button>
        ))}
      </div>
      {from === 'scratch' ? (
        <div className="de-form-grid">
          <label className="de-field">
            <span className="de-label">Name</span>
            <input className="de-input de-input--bordered" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Festive offer follow-up" />
          </label>
          <label className="de-field">
            <span className="de-label">Folder</span>
            <input className="de-input de-input--bordered" value={form.folder} onChange={(e) => setForm((f) => ({ ...f, folder: e.target.value }))} />
          </label>
          <label className="de-field">
            <span className="de-label">Starts when</span>
            <select className="de-select" value={form.trigger} onChange={(e) => setForm((f) => ({ ...f, trigger: e.target.value }))}>
              {catalog.triggers.map((t) => (
                <option key={t.type} value={t.type}>
                  {t.group} · {t.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}
      {from === 'recipe' ? (
        !recipes ? (
          <Loader />
        ) : (
          <div style={{ display: 'grid', gap: 8, maxHeight: 360, overflow: 'auto' }}>
            {recipes.map((r) => (
              <label key={r.key} className="de-card" style={{ display: 'flex', gap: 10, cursor: 'pointer', alignItems: 'flex-start', borderColor: recipe === r.key ? 'var(--de-primary, #dc2626)' : undefined }}>
                <input type="radio" name="recipe" checked={recipe === r.key} onChange={() => setRecipe(r.key)} style={{ marginTop: 4 }} />
                <span style={{ minWidth: 0 }}>
                  <span className="de-td-strong">{r.name}</span>
                  <span className="de-small de-muted" style={{ display: 'block' }}>{r.description}</span>
                  <span className="de-small de-muted" style={{ display: 'block' }}>
                    {r.folder} · starts when: {triggerLabel(r.trigger)} · {r.steps} step{r.steps === 1 ? '' : 's'}
                    {r.templates?.length ? ` · templates: ${r.templates.join(', ')}` : ''}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )
      ) : null}
      {from === 'copy' ? (
        <label className="de-field">
          <span className="de-label">Automation to copy</span>
          <select className="de-select" value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">Choose…</option>
            {rows.map((r) => (
              <option key={r.id} value={r.id}>
                {r.folder} · {r.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <div className="de-form-actions" style={{ marginTop: 18 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={!ready || busy} onClick={create}>
          {busy ? <Spinner light /> : null}
          Create draft and open
        </button>
      </div>
    </Modal>
  );
}

function AutomationsTab({ rows, canManage, catalog, reload, open }) {
  const { branchName, branches: allBranches } = useEngagementScope();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [runsAt, setRunsAt] = useState('');
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [resetting, setResetting] = useState(null);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try {
      await fn();
      toast.success(ok);
      await reload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy('');
    }
  };

  const turnOn = (row) => {
    if (!row.live_version) return api.publishAutomation(row.id, true);
    return api.activateAutomation(row.id);
  };
  const turnOff = (row) => api.pauseAutomation(row.id);

  const shown = rows.filter((r) => {
    const label = statusLabel(r) === 'on' ? 'active' : statusLabel(r);
    return (
      (!status || label === status) &&
      (!q || r.name.toLowerCase().includes(q.toLowerCase())) &&
      (!runsAt || runsFor(r.branches, runsAt))
    );
  });
  const folders = useMemo(() => {
    const out = {};
    shown.forEach((r) => {
      (out[r.folder || 'Custom'] = out[r.folder || 'Custom'] || []).push(r);
    });
    return out;
  }, [shown]);

  return (
    <div>
      <div className="de-row" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 12, justifyContent: 'space-between' }}>
        <div className="de-row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <ChipRow
            chips={[
              { key: '', label: 'All' },
              { key: 'active', label: 'On' },
              { key: 'paused', label: 'Paused' },
              { key: 'draft', label: 'Drafts' },
            ]}
            value={status}
            onChange={setStatus}
          />
          <SearchBox value={q} onChange={setQ} placeholder="Search automations…" />
          {allBranches.length > 1 ? (
            <div style={{ minWidth: 240, flex: '0 1 300px' }} title="Only the automations customers of this branch can enter">
              <BranchSelect branches={allBranches} value={runsAt} onChange={setRunsAt} emptyLabel="Runs for: every branch" placeholder="Runs for: every branch" />
            </div>
          ) : null}
        </div>
        {canManage ? (
          <button type="button" className="de-btn de-btn--primary" onClick={() => setCreating(true)}>
            + New automation
          </button>
        ) : null}
      </div>
      {!shown.length ? <EmptyState icon="🤖" title="No automations here" body="Create one from scratch or from a recipe." /> : null}
      {Object.entries(folders).map(([folder, items]) => (
        <div key={folder} style={{ marginBottom: 18 }}>
          <div className="de-section-label">{folder}</div>
          <div className="de-table-shell">
            <div className="de-table-wrap">
              <table className="de-table">
                <thead>
                  <tr>
                    <th>Automation</th>
                    <th>Starts when</th>
                    <th>Status</th>
                    <th>Last 30 days</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => {
                    const s = r.stats_30d || {};
                    const limit = branchLimit(r.branches, branchName);
                    return (
                      <tr key={r.id}>
                        <td className="de-td-strong" style={{ maxWidth: 340 }}>
                          {r.name} {r.is_starter ? <Pill tone="gold">Starter</Pill> : null}
                          {r.is_starter && r.edited_since_seed ? <span className="de-small de-muted"> · edited</span> : null}
                          {limit ? <Pill tone="info">📍 {limit}</Pill> : null}
                          <div className="de-small de-muted">{r.description}</div>
                          {r.templates_waiting?.length ? (
                            <div className="de-small de-warn-text">
                              Waiting for the message template {r.templates_waiting.join(', ')}. {' '}
                              <Link to={engagementPath('templates')}>Open message templates</Link>
                            </div>
                          ) : null}
                        </td>
                        <td className="de-small">
                          {r.trigger_label}
                          <div className="de-muted">{r.steps} step{r.steps === 1 ? '' : 's'}</div>
                        </td>
                        <td>
                          <Pill tone={STATUS_TONE[statusLabel(r)] || 'muted'}>{statusLabel(r)}</Pill>
                          {r.has_unpublished_changes && r.live_version ? <div className="de-small de-warn-text">Unpublished changes</div> : null}
                        </td>
                        <td className="de-small">
                          {s.entered || 0} entered · {s.running || 0} running
                          <div className="de-muted">
                            {s.sent || 0} sent · {s.read || 0} read{s.failed ? ` · ${s.failed} failed` : ''}
                          </div>
                        </td>
                        <td className="de-td-right" style={{ whiteSpace: 'nowrap' }}>
                          <div className="de-row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                            <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => open(r.id)}>
                              {canManage ? 'Edit' : 'View'}
                            </button>
                            {canManage && r.status === 'active' ? (
                              <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={busy === `p${r.id}`} onClick={() => act(`p${r.id}`, () => turnOff(r), 'Paused: nobody new enters')}>
                                Pause
                              </button>
                            ) : null}
                            {canManage && r.status !== 'active' ? (
                              <button
                                type="button"
                                className="de-btn de-btn--outline de-btn--sm"
                                disabled={busy === `a${r.id}` || Boolean(r.templates_waiting?.length)}
                                title={r.templates_waiting?.length ? 'The WhatsApp template is not approved yet' : ''}
                                onClick={() => act(`a${r.id}`, () => turnOn(r), 'Switched on')}
                              >
                                Switch on
                              </button>
                            ) : null}
                            {canManage ? (
                              <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={busy === `d${r.id}`} onClick={() => act(`d${r.id}`, () => api.duplicateAutomation(r.id), 'Copied as a draft')}>
                                Copy
                              </button>
                            ) : null}
                            {canManage && r.is_starter ? (
                              <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setResetting(r)}>
                                Reset
                              </button>
                            ) : null}
                            {canManage && !r.is_starter ? (
                              <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setDeleting(r)}>
                                {r.live_version ? 'Archive' : 'Delete'}
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ))}
      {creating ? (
        <NewAutomationModal
          catalog={catalog}
          rows={rows}
          onClose={() => setCreating(false)}
          onCreated={(row) => {
            setCreating(false);
            reload();
            open(row.id);
          }}
        />
      ) : null}
      {deleting ? (
        <Confirm
          title={`${deleting.live_version ? 'Archive' : 'Delete'} ${deleting.name}?`}
          message={deleting.live_version ? 'Nobody new enters; customers already in it finish. Its history stays.' : 'The draft is deleted.'}
          confirmLabel={deleting.live_version ? 'Archive' : 'Delete'}
          busy={busy === 'del'}
          onCancel={() => setDeleting(null)}
          onConfirm={() =>
            act('del', () => (deleting.live_version ? api.archiveAutomation(deleting.id) : api.deleteAutomation(deleting.id)), 'Done').finally(() => setDeleting(null))
          }
        />
      ) : null}
      {resetting ? (
        <Confirm
          title={`Reset ${resetting.name}?`}
          message="Its steps, timings, branches and templates go back to the starter's own and are published now. It stays on or paused as it is. Customers already in it finish on the version they started."
          confirmLabel="Reset to starter"
          busy={busy === 'reset'}
          onCancel={() => setResetting(null)}
          onConfirm={() => act('reset', () => api.resetAutomation(resetting.id), 'Back to the starter steps').finally(() => setResetting(null))}
        />
      ) : null}
    </div>
  );
}

// ── runs ─────────────────────────────────────────────────────────────────────

function RunModal({ runId, canManage, onClose }) {
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState('');
  const load = () =>
    api
      .getRun(runId)
      .then(setRun)
      .catch((e) => toast.error(e.message));
  useEffect(() => {
    load();
  }, [runId]);
  const act = async (key, fn, ok) => {
    setBusy(key);
    try {
      await fn();
      toast.success(ok);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy('');
    }
  };
  if (!run) return null;
  const running = ['active', 'waiting'].includes(run.status);
  return (
    <Modal title={`${run.automation_name} · ${run.customer_name || formatPhone(run.customer_phone)}`} onClose={onClose} size="wide">
      <div className="de-row" style={{ gap: 10, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        <Pill tone={RUN_TONE[run.status]}>{run.status}</Pill>
        {run.is_test ? <Pill tone="info">test</Pill> : null}
        <span className="de-small de-muted">
          Started {formatDateTime(run.started_at)}
          {run.next_run_at && running ? ` · next step ${formatDateTime(run.next_run_at)}` : ''}
          {run.stop_reason ? ` · ${run.stop_reason}` : ''}
        </span>
        {canManage ? (
          <div className="de-row" style={{ gap: 6, marginLeft: 'auto' }}>
            {running ? (
              <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={busy === 'p'} onClick={() => act('p', () => api.pauseRun(run.id), 'Paused')}>
                Pause
              </button>
            ) : null}
            {run.status === 'paused' ? (
              <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={busy === 'r'} onClick={() => act('r', () => api.resumeRun(run.id), 'Resumed')}>
                Resume
              </button>
            ) : null}
            {running || run.status === 'paused' ? (
              <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={busy === 's'} onClick={() => act('s', () => api.stopRun(run.id), 'Stopped')}>
                Stop
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="de-section-label">Messages</div>
      {run.messages.length ? (
        <div className="de-table-wrap" style={{ marginBottom: 14 }}>
          <table className="de-table">
            <thead>
              <tr>
                <th>Step</th>
                <th>Message</th>
                <th>Status</th>
                <th>When</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {run.messages.map((m) => (
                <tr key={m.id}>
                  <td className="de-mono de-small">{m.step}</td>
                  <td className="de-small" style={{ maxWidth: 360 }}>
                    <b>{m.template}</b>
                    <div className="de-muted">{(m.body || '').slice(0, 160)}</div>
                  </td>
                  <td className="de-small">
                    {m.status}
                    {m.sent_as ? ` (${m.sent_as})` : ''}
                    {m.error ? <div className="de-danger-text">{m.error}</div> : null}
                  </td>
                  <td className="de-small">{formatDateTime(m.sent_at || m.send_at)}</td>
                  <td>
                    {canManage && ['failed', 'cancelled'].includes(m.status) ? (
                      <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={busy === `j${m.id}`} onClick={() => act(`j${m.id}`, () => api.retryJob(m.id), 'Queued again')}>
                        Retry
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="de-small de-muted" style={{ marginBottom: 14 }}>
          None yet.
        </div>
      )}
      <div className="de-section-label">Steps</div>
      <div style={{ maxHeight: 280, overflow: 'auto' }}>
        {run.events.map((e, i) => (
          <div key={i} className="de-small" style={{ padding: '4px 0', borderBottom: '1px solid var(--de-border, #eee)' }}>
            <span className="de-muted">{formatDateTime(e.at)}</span> · <span className="de-mono">{e.step || '—'}</span> · <b>{e.event}</b> {e.detail}
          </div>
        ))}
      </div>
    </Modal>
  );
}


function RunsTab({ automations, canManage }) {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState({ automation_id: '', status: 'open', q: '' });
  const [openRun, setOpenRun] = useState(null);
  const load = (offset = 0) => {
    if (offset) setLoadingMore(true);
    return api
      .listRuns({ ...filter, limit: RUNS_PAGE, offset })
      .then((page) => {
        setTotal(page.total || 0);
        setRows((current) => (offset ? [...(current || []), ...(page.items || [])] : page.items || []));
      })
      .catch((e) => toast.error(e.message))
      .finally(() => setLoadingMore(false));
  };
  useEffect(() => {
    load();
  }, [filter.automation_id, filter.status]);
  return (
    <div>
      <div className="de-row" style={{ gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <select className="de-select" style={{ width: 260 }} value={filter.automation_id} onChange={(e) => setFilter((f) => ({ ...f, automation_id: e.target.value }))}>
          <option value="">All automations</option>
          {automations.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
        <ChipRow
          chips={[
            { key: 'open', label: 'In a flow' },
            { key: '', label: 'All' },
            { key: 'waiting', label: 'Waiting' },
            { key: 'completed', label: 'Completed' },
            { key: 'stopped', label: 'Stopped' },
            { key: 'failed', label: 'Failed' },
          ]}
          value={filter.status}
          onChange={(status) => setFilter((f) => ({ ...f, status }))}
        />
        <SearchBox value={filter.q} onChange={(q) => setFilter((f) => ({ ...f, q }))} onEnter={() => load()} placeholder="Customer name or phone, then Enter" />
      </div>
      {!rows ? (
        <Loader />
      ) : rows.length ? (
        <>
          <div className="de-table-shell">
            <div className="de-table-wrap">
              <table className="de-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Automation</th>
                    <th>Status</th>
                    <th>Step</th>
                    <th>Started</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="de-td-strong">
                        {r.customer_name || '—'}
                        <div className="de-small de-muted">{formatPhone(r.customer_phone)}</div>
                      </td>
                      <td className="de-small">
                        {r.automation_name}
                        {r.is_test ? <Pill tone="info">test</Pill> : null}
                      </td>
                      <td>
                        <Pill tone={RUN_TONE[r.status]}>{r.status}</Pill>
                        {r.stop_reason ? <div className="de-small de-muted">{r.stop_reason}</div> : null}
                      </td>
                      <td className="de-small de-mono">
                        {r.current_step}
                        {r.next_run_at && ['waiting', 'active'].includes(r.status) ? <div className="de-muted">{formatDateTime(r.next_run_at)}</div> : null}
                      </td>
                      <td className="de-small">{formatDateTime(r.started_at)}</td>
                      <td className="de-td-right">
                        <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setOpenRun(r.id)}>
                          Open
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="de-row" style={{ gap: 10, marginTop: 10, alignItems: 'center' }}>
            <span className="de-small de-muted">
              Showing {rows.length} of {total}
            </span>
            {rows.length < total ? (
              <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={loadingMore} onClick={() => load(rows.length)}>
                {loadingMore ? <Spinner /> : null}
                Show more
              </button>
            ) : null}
          </div>
        </>
      ) : (
        <EmptyState icon="🏃" title="No runs" body="Customers enter an automation when its trigger happens." />
      )}
      {openRun ? (
        <RunModal
          runId={openRun}
          canManage={canManage}
          onClose={() => {
            setOpenRun(null);
            load();
          }}
        />
      ) : null}
    </div>
  );
}

// ── safety: the organization's sending rules ─────────────────────────────────

function SafetyCard({ title, children, sub }) {
  return (
    <div className="de-card" style={{ marginBottom: 16 }}>
      <div className="de-td-strong">{title}</div>
      {sub ? <div className="de-small de-muted" style={{ margin: '6px 0 12px', lineHeight: 1.5 }}>{sub}</div> : null}
      {children}
    </div>
  );
}

function SafetyTab({ canManage }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState('');
  const take = (d) => {
    setData(d);
    setDraft({
      test_numbers: (d.test_numbers || []).join(', '),
      marketing_limit_hours: d.marketing_limit_hours,
      quiet_hours: { ...d.quiet_hours },
      no_show: { ...d.no_show },
    });
  };
  useEffect(() => {
    api
      .automationEngineSettings()
      .then(take)
      .catch((e) => toast.error(e.message));
  }, []);
  const save = async (key, changes, ok = 'Saved') => {
    setBusy(key);
    try {
      const d = await api.saveAutomationEngineSettings(changes);
      take(d);
      toast.success(d.stopped ? `${ok}. ${d.stopped} waiting marketing messages stopped` : ok);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy('');
    }
  };
  if (!data || !draft) return <Loader />;
  const setQuiet = (patch) => setDraft((d) => ({ ...d, quiet_hours: { ...d.quiet_hours, ...patch } }));
  const setNoShow = (patch) => setDraft((d) => ({ ...d, no_show: { ...d.no_show, ...patch } }));
  const off = !canManage || Boolean(busy);
  return (
    <div style={{ maxWidth: 720 }}>
      <div className="de-small de-muted" style={{ marginBottom: 12 }}>
        These rules apply to every automation and every branch of the organization. Which messages go out, and when, is set in each automation.
      </div>
      <SafetyCard title="Marketing messages" sub="Pausing stops every marketing message from automations at once (offers, win-backs, nudges). Booking messages keep going.">
        <button
          type="button"
          className={`de-btn ${data.marketing_paused ? 'de-btn--primary' : 'de-btn--danger'}`}
          disabled={off}
          onClick={() => save('pause', { marketing_paused: !data.marketing_paused }, data.marketing_paused ? 'Marketing messages resumed' : 'Marketing messages paused')}
        >
          {data.marketing_paused ? 'Resume marketing messages' : 'Pause all marketing messages'}
        </button>
      </SafetyCard>
      <SafetyCard
        title="Message limit per customer"
        sub="At most one marketing message per customer in this many hours, across every branch and number. A second one waits until the time has passed. 0 = no limit. Booking messages are never limited."
      >
        <div className="de-row" style={{ gap: 8, alignItems: 'center' }}>
          <span className="de-small">One marketing message every</span>
          <input
            className="de-input de-input--bordered"
            type="number"
            min={0}
            max={168}
            style={{ width: 90 }}
            value={draft.marketing_limit_hours}
            disabled={!canManage}
            onChange={(e) => setDraft((d) => ({ ...d, marketing_limit_hours: e.target.value }))}
          />
          <span className="de-small">hours</span>
          <button
            type="button"
            className="de-btn de-btn--primary de-btn--sm"
            disabled={off || String(draft.marketing_limit_hours) === String(data.marketing_limit_hours)}
            onClick={() => save('limit', { marketing_limit_hours: Number(draft.marketing_limit_hours) })}
          >
            Save
          </button>
        </div>
      </SafetyCard>
      <SafetyCard title="🌙 Quiet hours" sub="A message that follows quiet hours and falls inside them goes out when they end. Booking confirmations, appointment reminders and feedback requests are never moved.">
        <div className="de-row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Switch on={draft.quiet_hours.enabled} label="Quiet hours" disabled={!canManage} onChange={(enabled) => setQuiet({ enabled })} />
          <span className="de-small">Do not send between</span>
          <input className="de-input de-input--bordered" type="time" style={{ width: 120 }} value={draft.quiet_hours.start} disabled={!canManage || !draft.quiet_hours.enabled} onChange={(e) => setQuiet({ start: e.target.value })} />
          <span className="de-small">and</span>
          <input className="de-input de-input--bordered" type="time" style={{ width: 120 }} value={draft.quiet_hours.end} disabled={!canManage || !draft.quiet_hours.enabled} onChange={(e) => setQuiet({ end: e.target.value })} />
          <span className="de-small">salon time.</span>
          <button
            type="button"
            className="de-btn de-btn--primary de-btn--sm"
            disabled={off || JSON.stringify(draft.quiet_hours) === JSON.stringify(data.quiet_hours)}
            onClick={() => save('quiet', { quiet_hours: draft.quiet_hours })}
          >
            Save
          </button>
        </div>
        {data.quiet_hours_text ? <div className="de-small de-muted de-mt-8">Now: {data.quiet_hours_text}</div> : null}
      </SafetyCard>
      <SafetyCard title="✋ Did they come?" sub="Appointments still Booked after their time. Staff answer with one tap on Appointments; at closing time the rest of today's are marked No-show, which starts the Missed appointment automation.">
        <div className="de-row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
          <Switch on={draft.no_show.check_enabled} label="Ask staff" disabled={!canManage} onChange={(check_enabled) => setNoShow({ check_enabled })} />
          <span className="de-small">List appointments still Booked</span>
          <input className="de-input de-input--bordered" type="number" min={5} max={720} style={{ width: 90 }} value={draft.no_show.check_minutes} disabled={!canManage || !draft.no_show.check_enabled} onChange={(e) => setNoShow({ check_minutes: e.target.value })} />
          <span className="de-small">minutes after their start time.</span>
        </div>
        <div className="de-row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Switch on={draft.no_show.auto_enabled} label="Automatic No-show" disabled={!canManage} onChange={(auto_enabled) => setNoShow({ auto_enabled })} />
          <span className="de-small">At</span>
          <input className="de-input de-input--bordered" type="time" style={{ width: 120 }} value={draft.no_show.auto_time} disabled={!canManage || !draft.no_show.auto_enabled} onChange={(e) => setNoShow({ auto_time: e.target.value })} />
          <span className="de-small">each day, mark today's appointments nobody updated as No-show.</span>
          <button
            type="button"
            className="de-btn de-btn--primary de-btn--sm"
            disabled={off || JSON.stringify(draft.no_show) === JSON.stringify(data.no_show)}
            onClick={() => save('noshow', { no_show: { ...draft.no_show, check_minutes: Number(draft.no_show.check_minutes) } })}
          >
            Save
          </button>
        </div>
      </SafetyCard>
      <SafetyCard title="Skin / hair report" sub="The analysis report PDF sent when a salon check-in visit with a skin or hair session is completed (one message per report, with the file).">
        <div className="de-row" style={{ gap: 10, alignItems: 'center' }}>
          <Switch on={data.visit_report.enabled} label="Visit report" disabled={off} onChange={(enabled) => save('report', { visit_report: { enabled } }, enabled ? 'Report message on' : 'Report message off')} />
          <span className="de-small">{data.visit_report.enabled ? 'Sent' : 'Not sent'}</span>
        </div>
      </SafetyCard>
      <SafetyCard title="Test numbers" sub="Numbers your team uses to try automations before switching them on.">
        <input className="de-input de-input--bordered" value={draft.test_numbers} disabled={!canManage} onChange={(e) => setDraft((d) => ({ ...d, test_numbers: e.target.value }))} placeholder="9876543210, 9123456780" />
        <div className="de-form-actions de-mt-8">
          <button
            type="button"
            className="de-btn de-btn--primary"
            disabled={off}
            onClick={() => save('numbers', { test_numbers: draft.test_numbers.split(',').map((s) => s.trim()).filter(Boolean) })}
          >
            Save
          </button>
        </div>
      </SafetyCard>
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

export default function Automations() {
  const { canManage } = useEngagementScope();
  const [params, setParams] = useSearchParams();
  const asked = TAB_ALIASES[params.get('tab')] || params.get('tab');
  const tab = TABS.some((t) => t.id === asked) ? asked : 'automations';
  const setTab = (id) => {
    const next = new URLSearchParams(params);
    if (!id || id === 'automations') next.delete('tab');
    else next.set('tab', id);
    setParams(next, { replace: true });
  };
  const [rows, setRows] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () =>
    api
      .listAutomations()
      .then(setRows)
      .catch((e) => toast.error(e.message));

  useEffect(() => {
    load();
    api.automationCatalog().then(setCatalog).catch((e) => toast.error(e.message));
  }, [canManage]);

  if (!rows || !catalog) return <Loader />;
  const visibleTabs = canManage ? TABS : TABS.filter((t) => t.id !== 'safety');
  return (
    <div className="de-fade">
      <PageHead title="Automations" sub="Every WhatsApp message the salon sends on its own. Open one to change the steps, or create a new one." />
      <div className="de-tabs" style={{ borderRadius: 0 }}>
        {visibleTabs.map((t) => (
          <button key={t.id} type="button" className={`de-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
            <span style={{ marginRight: 6 }}>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>
      <div className="de-tab-body">
        {tab === 'automations' ? (
          <AutomationsTab rows={rows} canManage={canManage} catalog={catalog} reload={load} open={setEditing} />
        ) : null}
        {tab === 'messages' ? <ReminderMessages automations={rows} /> : null}
        {tab === 'runs' ? <RunsTab automations={rows} canManage={canManage} /> : null}
        {tab === 'safety' ? <SafetyTab canManage={canManage} /> : null}
      </div>
      {editing ? (
        <AutomationEditor
          automationId={editing}
          catalog={catalog}
          onClose={() => {
            setEditing(null);
            load();
          }}
          onChanged={() => {
            load();
          }}
        />
      ) : null}
    </div>
  );
}
